import React, { useState } from "react";
import cn from "classnames";
import moment from "moment";
import { toast } from "react-toastify";
import style from "./ClientBidChangeCard.module.scss";
import { Order } from "../../../../../../../services";
import { numberToFinanceFormat } from "../../../../../../../utils";
import ChevronIcon from "../../../../../../shared/icons/ChevronIcon";
import { useTranslation } from "react-i18next";
import InfoIcon from "../../../../../../shared/icons/InfoIcon";
import ArrowBidIcon from "../../../../../../shared/icons/ArrowBidIcon";

// awaiting — ожидает решения клиента; confirmed/rejected — финальные статусы
const STATUS_BADGE = {
  awaiting: { key: "awaiting", cls: "badge_awaiting" },
  confirmed: { key: "confirmed", cls: "badge_confirmed" },
  rejected: { key: "rejected", cls: "badge_rejected" },
};

// changedPriceText приходит в виде "былоЦена - стало ₸" — разбираем на «Было»/«Стало»
const parseChangedPrice = (text) => {
  const raw = (text || "").trim();
  if (!raw) return { oldPrice: "", newPrice: "", symbol: "" };

  const symbolMatch = raw.match(/[^\d\s.,]+$/);
  const symbol = symbolMatch ? symbolMatch[0].trim() : "";
  const numbersPart = symbolMatch ? raw.slice(0, symbolMatch.index) : raw;
  const parts = numbersPart
    .split(/->|→|—|–|-|>/)
    .map((s) => s.trim())
    .filter(Boolean);

  const fmt = (s) => {
    const n = Number(s.replace(/[^\d.]/g, ""));
    return Number.isFinite(n) && n > 0 ? numberToFinanceFormat(n, true) : s;
  };

  return {
    oldPrice: parts[0] ? fmt(parts[0]) : "",
    newPrice: parts[1] ? fmt(parts[1]) : parts[0] ? fmt(parts[0]) : "",
    symbol,
  };
};

const ClientBidChangeCard = ({ item, onDone, canDecide = true }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const status = item?.changedPriceStatus;
  const badge = STATUS_BADGE[status];
  const isAwaiting = status === "awaiting";
  const { oldPrice, newPrice, symbol } = parseChangedPrice(item?.changedPriceText);
  const decisionNote =
    status === "rejected"
      ? t("orderInfoClient.bidChange.decisionRejected", { price: newPrice, currency: symbol })
      : status === "confirmed"
      ? t("orderInfoClient.bidChange.decisionConfirmed", { price: newPrice, currency: symbol })
      : "";
  const newPriceNum = Number((item?.changedPriceText || "").split(/->|→|—|–|-|>/)[1]?.replace(/[^\d.]/g, "")) || 0;
  const dateStr = item?.createdAt ? moment(item.createdAt).format("DD.MM.YYYY, HH:mm") : "";

  const handleDecision = (type) => {
    if (!item?.orderBidId || loading) return;
    setLoading(true);
    Order.decideNewPrice(type, item.orderBidId, { price: newPriceNum })
      .then(() => {
        toast.success(
          type === "accepted"
            ? t("orderInfoClient.bidChange.acceptSuccess")
            : t("orderInfoClient.bidChange.rejectSuccess"),
        );
        onDone?.();
      })
      .catch(() => toast.error(t("orderInfoClient.bidChange.decisionError")))
      .finally(() => setLoading(false));
  };

  return (
    <div className={cn(style.card, { [style.card_awaiting]: isAwaiting })}>
      <div
        className={cn(style.header, { [style.header_open]: open })}
        onClick={() => setOpen((v) => !v)}
      >
        <div className={style.header_left}>
          <span className={style.title}>{t("orderInfoClient.bidChange.title")}</span>
          {badge && (
            <span className={cn(style.badge, style[badge.cls])}>
              <span className={style.badge_dot} />
              {t(`orderInfoClient.bidChange.status.${badge.key}`)}
            </span>
          )}
        </div>
        <div className={style.header_right}>
          {dateStr && <span className={style.date}>{dateStr}</span>}
          <span className={cn(style.chevron, { [style.chevron_open]: open })}>
            <ChevronIcon />
          </span>
        </div>
      </div>

      {open && (
        <div className={style.body}>
          <div className={style.compare}>
            <div className={style.compare_col}>
              <span className={style.compare_label}>{t("orderInfoClient.bidChange.before")}</span>
              <span className={cn(style.compare_value, style.compare_old)}>
                {oldPrice} {symbol}
              </span>
            </div>
            <span className={style.compare_arrow}>
              <ArrowBidIcon />
            </span>
            <div className={style.compare_col}>
              <span className={style.compare_label}>{t("orderInfoClient.bidChange.after")}</span>
              <span className={cn(style.compare_value, style.compare_new)}>
                {newPrice} {symbol}
              </span>
            </div>
          </div>

          {item?.comment && (
            <div className={style.reason}>
              <InfoIcon type={2} />
              <span className={style.reason_text}>
                {t("orderInfoClient.bidChange.reason", { comment: item.comment })}
              </span>
            </div>
          )}

          {decisionNote && (
            <p
              className={cn(style.decision_note, {
                [style.decision_note_rejected]: status === "rejected",
                [style.decision_note_confirmed]: status === "confirmed",
              })}
            >
              {decisionNote}
            </p>
          )}

          {canDecide && status !== "rejected" && status !== "confirmed" && (
            <div className={style.actions}>
              <button
                type="button"
                className={style.btn_accept}
                onClick={() => handleDecision("accepted")}
                disabled={loading}
              >
                {t("orderInfoClient.bidChange.accept")}
              </button>
              <button
                type="button"
                className={style.btn_reject}
                onClick={() => handleDecision("rejected")}
                disabled={loading}
              >
                {t("orderInfoClient.bidChange.reject")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientBidChangeCard;
